import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../../backend/firebaseConfig";

const normaliseEmail = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase();

const getAcknowledgementDetails = (announcement) => {
  return Array.isArray(announcement?.acknowledgementDetails)
    ? announcement.acknowledgementDetails
    : [];
};

const getUserEmailsForStats = (announcement) => {
  const resourceClickedUsers = Array.isArray(announcement?.resourceClickedUsers)
    ? announcement.resourceClickedUsers
    : [];

  const acknowledgementUsers = getAcknowledgementDetails(announcement)
    .map((item) => item.userEmail || item.email)
    .filter(Boolean);

  const lastClickedBy = announcement?.lastResourceClickedBy
    ? [announcement.lastResourceClickedBy]
    : [];

  return Array.from(
    new Set(
      [...resourceClickedUsers, ...acknowledgementUsers, ...lastClickedBy]
        .map(normaliseEmail)
        .filter(Boolean),
    ),
  );
};

const formatDateTime = (row) => {
  const timestamp = row.actionAtMs || 0;

  if (timestamp) {
    return dayjs(timestamp).format("DD/MM/YYYY, h:mm a");
  }

  const firestoreDate = row.actionAt?.toDate?.();

  if (firestoreDate) {
    return dayjs(firestoreDate).format("DD/MM/YYYY, h:mm a");
  }

  return "-";
};

const formatUser = (row) => {
  const name =
    typeof row.userName === "string"
      ? row.userName
      : row.userName?.name ||
        row.userName?.displayName ||
        row.userName?.fullName ||
        "";

  const email = row.userEmail || row.email || "";

  if (name && email) return `${name} (${email})`;
  if (email) return email;
  if (name) return name;

  return "-";
};

const getDaysActive = (announcement) => {
  const start =
    announcement?.postedAtMs ||
    announcement?.createdAtMs ||
    announcement?.createdAt?.toDate?.()?.getTime?.();

  if (!start) return 0;

  return Math.max(dayjs().diff(dayjs(start), "day"), 0);
};

const buildFallbackRows = (announcement) => {
  const rows = [];

  const resourceUsers = Array.isArray(announcement?.resourceClickedUsers)
    ? announcement.resourceClickedUsers
    : [];

  resourceUsers.forEach((email) => {
    const safeEmail = normaliseEmail(email);

    if (!safeEmail) return;

    rows.push({
      id: `fallback-click-${safeEmail}`,
      type: "read-more",
      label: "Read More",
      email: safeEmail,
      userEmail: safeEmail,
      userName: "",
      userRole: "",
      actionAt: announcement?.lastResourceClickAt || null,
      actionAtMs:
        announcement?.lastResourceClickAt?.toDate?.()?.getTime?.() ||
        announcement?.updatedAt?.toDate?.()?.getTime?.() ||
        0,
      clickCount: "-",
      isFallback: true,
    });
  });

  getAcknowledgementDetails(announcement).forEach((item, index) => {
    const safeEmail = normaliseEmail(item.userEmail || item.email);

    if (!safeEmail) return;

    rows.push({
      id: `fallback-ack-${safeEmail}-${index}`,
      type: "acknowledged",
      label: "Acknowledged",
      email: safeEmail,
      userEmail: safeEmail,
      userName: item.userName || item.name || "",
      userRole: item.userRole || "",
      actionAt: item.acknowledgedAt || null,
      actionAtMs:
        item.acknowledgedAtMs ||
        item.acknowledgedAt?.toDate?.()?.getTime?.() ||
        0,
      clickCount: "-",
      isFallback: true,
    });
  });

  return rows;
};

const AnnouncementStatsTab = ({ announcement }) => {
  const [liveRows, setLiveRows] = useState([]);

  const userEmails = useMemo(
    () => getUserEmailsForStats(announcement),
    [announcement],
  );

  const userEmailsKey = userEmails.join("|");

  useEffect(() => {
    if (!announcement?.id || userEmails.length === 0) {
      setLiveRows([]);
      return;
    }

    const rowMap = new Map();

    const updateRows = () => {
      setLiveRows(
        Array.from(rowMap.values()).sort(
          (a, b) => (b.actionAtMs || 0) - (a.actionAtMs || 0),
        ),
      );
    };

    const unsubscribers = [];

    userEmails.forEach((email) => {
      const resourceClickRef = doc(
        db,
        "Marketing",
        "Announcements",
        "UserResourceClicks",
        email,
        "Items",
        announcement.id,
      );

      const acknowledgementRef = doc(
        db,
        "Marketing",
        "Announcements",
        "UserAcknowledgements",
        email,
        "Items",
        announcement.id,
      );

      const unsubscribeResourceClick = onSnapshot(resourceClickRef, (snap) => {
        const key = `click-${email}`;

        if (!snap.exists()) {
          rowMap.delete(key);
          updateRows();
          return;
        }

        const data = snap.data();

        const actionAt =
          data.lastClickedAt || data.clickedAt || data.firstClickedAt || null;

        const actionAtMs =
          data.lastClickedAtMs ||
          data.clickedAtMs ||
          data.firstClickedAtMs ||
          actionAt?.toDate?.()?.getTime?.() ||
          0;

        rowMap.set(key, {
          id: key,
          type: "read-more",
          label: "Read More",
          email,
          userEmail: data.userEmail || email,
          userName: data.userName || "",
          userRole: data.userRole || "",
          actionAt,
          actionAtMs,
          clickCount: Number(data.clickCount || 1),
          source: data.source || "",
          actionType: data.actionType || "",
          actionUrl: data.actionUrl || "",
        });

        updateRows();
      });

      const unsubscribeAcknowledgement = onSnapshot(
        acknowledgementRef,
        (snap) => {
          const key = `ack-${email}`;

          if (!snap.exists()) {
            rowMap.delete(key);
            updateRows();
            return;
          }

          const data = snap.data();

          const actionAt = data.acknowledgedAt || null;

          const actionAtMs =
            data.acknowledgedAtMs || actionAt?.toDate?.()?.getTime?.() || 0;

          rowMap.set(key, {
            id: key,
            type: "acknowledged",
            label: "Acknowledged",
            email,
            userEmail: data.userEmail || email,
            userName: data.userName || "",
            userRole: data.userRole || "",
            actionAt,
            actionAtMs,
            clickCount: "-",
          });

          updateRows();
        },
      );

      unsubscribers.push(unsubscribeResourceClick, unsubscribeAcknowledgement);
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe?.());
    };
  }, [announcement?.id, userEmailsKey]);

  const fallbackRows = useMemo(
    () => buildFallbackRows(announcement),
    [announcement],
  );

  const rows = liveRows.length > 0 ? liveRows : fallbackRows;

  const acknowledgementRows = rows.filter((row) => row.type === "acknowledged");
  const resourceClickRows = rows.filter((row) => row.type === "read-more");

  const acknowledgedUsers = new Set(
    acknowledgementRows
      .map((row) => row.userEmail || row.email)
      .filter(Boolean),
  ).size;

  const usersClicked = new Set(
    resourceClickRows.map((row) => row.userEmail || row.email).filter(Boolean),
  ).size;

  const totalResourceClicks = resourceClickRows.reduce((total, row) => {
    if (typeof row.clickCount === "number") {
      return total + row.clickCount;
    }

    return total;
  }, 0);

  const daysActive = getDaysActive(announcement);

  return (
    <div className="announcement-stats-tab">
      <section className="announcement-stats-summary">
        <div className="announcement-summary-card announcement-summary-card--blue">
          <span className="FP_Help_Text">Total Resource Clicks</span>
          <strong className="FP_H2">
            {announcement?.resourceClickCount || totalResourceClicks}
          </strong>
        </div>

        <div className="announcement-summary-card announcement-summary-card--green">
          <span className="FP_Help_Text">
            Acknowledged Users • Users Clicked
          </span>
          <strong className="FP_H2">
            {announcement?.acknowledgementCount || acknowledgedUsers}{" "}•{" "}
            {announcement?.uniqueResourceClickUserCount || usersClicked}
          </strong>
        </div>

        <div className="announcement-summary-card announcement-summary-card--yellow">
          <span className="FP_Help_Text">Days Active</span>
          <strong className="FP_H2">{daysActive}</strong>
        </div>
      </section>

      <section className="announcement-stats-table-card">
        <table className="announcement-stats-table">
          <thead>
            <tr>
              <th className="FP_Help_Text_Semibold">Actioned On</th>
              {/* <th className="FP_Help_Text_Semibold">User</th> */}
              <th className="FP_Help_Text_Semibold">Email</th>
              {/* <th className="FP_Help_Text_Semibold">Role</th> */}
              <th className="FP_Help_Text_Semibold">Type</th>
              <th className="FP_Help_Text_Semibold">Clicks</th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="announcement-stats-empty FP_Content">
                  No user stats yet.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id}>
                  <td className="FP_Content">{formatDateTime(row)}</td>
                  {/* <td className="FP_Content">{formatUser(row)}</td> */}
                  <td className="FP_Content">
                    {row.userEmail || row.email || "-"}
                  </td>
                  {/* <td className="FP_Content">{row.userRole || "-"}</td> */}
                  <td className="FP_Content">{row.label}</td>
                  <td className="FP_Content">
                    {row.type === "read-more" ? row.clickCount || 1 : "-"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
};

export default AnnouncementStatsTab;
